import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './PaymentSuccessPage.scss';
import { localStore, readCookies } from '../../components/utils/safeStorage';

const PaymentSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirectUrl, setRedirectUrl] = useState<string>('/');
  
  useEffect(() => {
    // Get credits from query params or fetch from API
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    // Fetch user's updated credit balance
    fetchCredits();
    
    // Get the return URL from localStorage or default to home
    const returnUrl = localStore.getItem('stripe_return_url') || '/';
    localStore.removeItem('stripe_return_url'); // Clean up
    
    console.log('PaymentSuccessPage - Return URL from localStorage:', returnUrl);
    
    // Add payment=success parameter to the return URL
    let finalRedirectUrl: string;
    if (returnUrl.includes('?')) {
      // URL already has query params, append payment param
      finalRedirectUrl = `${returnUrl}&payment=success`;
    } else {
      // No query params yet, add payment param
      finalRedirectUrl = `${returnUrl}?payment=success`;
    }
    
    console.log('PaymentSuccessPage - Final redirect URL:', finalRedirectUrl);
    setRedirectUrl(finalRedirectUrl);
    
    // Redirect after 5 seconds
    const timer = setTimeout(() => {
      history.push(finalRedirectUrl);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [history]);
  
  const fetchCredits = async () => {
    try {
      // Get cookie function
      const getCookie = (name: string) => {
        const value = `; ${readCookies()}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop()?.split(';').shift();
        return null;
      };
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/user-credits`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCredits(data.credits);
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="payment-success-page">
      <div className="payment-success-page__container">
        <div className="payment-success-page__content">
          <div className="payment-success-page__icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12l2 2l4 -4" stroke="#48c774" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="9" stroke="#48c774" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          
          <h1 className="payment-success-page__title">
            {t('payment.success.title', { defaultValue: 'Payment Successful!' })}
          </h1>
          
          <p className="payment-success-page__message">
            {t('payment.success.message', { 
              defaultValue: 'Your purchase of 60 AI image enhancement credits has been completed successfully.',
            })}
          </p>
          
          {!loading && credits !== null && (
            <div className="payment-success-page__credits">
              <div className="payment-success-page__credits-badge">
                {t('payment.success.creditsAvailable', {
                  count: credits,
                  defaultValue: `{{count}} credits available`,
                })}
              </div>
            </div>
          )}
          
          <p className="payment-success-page__redirect">
            {t('payment.success.redirecting', {
              defaultValue: 'Redirecting you back to the app...',
            })}
          </p>
          
          <button
            className="payment-success-page__button"
            onClick={() => history.push(redirectUrl)}
          >
            {t('payment.success.continueButton', { defaultValue: 'Continue to App' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
